import React from 'react';
import { useNavigate } from 'react-router-dom';
import { SparklesIcon as SolidSparklesIcon } from '@heroicons/react/24/solid';
import { Button } from '@oysterjs/ui/button';
import {
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
  MountableDialog
} from '@oysterjs/ui/dialog';

export type ProUpgradeUseCase = {
  id: string;
  icon: React.ReactNode;
  title: string;
  description: string;
};

/**
 * Pro upgrade prompt — same layout as Custom Forms / Pro preset gates in the library and home.
 */
export const ProUpgradeDialog = (props: {
  open: boolean;
  onClose: () => void;
  title: string;
  description: string;
  useCases?: ProUpgradeUseCase[];
}) => {
  const navigate = useNavigate();

  if (!props.open) {
    return null;
  }

  return (
    <MountableDialog onClose={props.onClose} size="lg">
      <div className="flex items-center gap-3">
        <div className="inline-flex shrink-0 items-center justify-center rounded-xl bg-amber-100 dark:bg-amber-500/20 p-2.5">
          <SolidSparklesIcon className="size-5 text-amber-600 dark:text-amber-400" />
        </div>
        <DialogTitle className="text-wrap m-0">{props.title}</DialogTitle>
      </div>
      <DialogDescription>{props.description}</DialogDescription>
      {props.useCases && props.useCases.length > 0 && (
        <DialogBody>
          <div className="space-y-4">
            {props.useCases.map((useCase) => (
              <div
                key={useCase.id}
                className="flex items-start gap-3 rounded-lg border border-neutral-200 dark:border-neutral-700 p-3"
              >
                <div className="shrink-0">{useCase.icon}</div>
                <div>
                  <p className="m-0 text-sm font-semibold text-neutral-900 dark:text-white">
                    {useCase.title}
                  </p>
                  <p className="m-0 mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                    {useCase.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </DialogBody>
      )}
      <DialogActions>
        <Button color="white" onClick={props.onClose}>
          Maybe Later
        </Button>
        <Button
          color="sky"
          onClick={() => {
            props.onClose();
            navigate('/app/settings/subscription');
          }}
        >
          <SolidSparklesIcon />
          Upgrade to Pro
        </Button>
      </DialogActions>
    </MountableDialog>
  );
};
